/* eslint-disable no-undef */
import FilePicker from "components/FilePicker";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Select from "react-select";
import { toast } from "react-toastify";
import { Collapse, Table, Tooltip } from "reactstrap";
import newApi from "service/newApi";
import { FormControlLabel, Switch } from "@mui/material";

const MAX_FILE_SIZE_MB = +process.env.REACT_APP_MAX_EVENT_FILE_SIZE_IN_MB || 8;
const MAX_CSV_FILES_LIMIT = +process.env.REACT_APP_MAX_CSV_FILES_LIMIT || 1;

const metadataSourceOptions = [
  { value: "csv", label: "Texto (CSV)" },
  { value: "query", label: "Query" },
  { value: "local", label: "Carregar arquivo" },
];

const selectStyles = {
  control: (base) => ({ ...base, fontSize: "14px" }),
  menu: (base) => ({ ...base, fontSize: "14px" }),
};

function CSVComponent() {
  const { watch, setValue } = useFormContext();
  const metadataSourceFiles = watch("metadata_source_files") || [];
  const metadataColumns = watch("metadata_columns") || [];
  const folderId = watch("data_source_folder_id");
  const csvReference = watch("csv_event_file_path");
  const selectedClient = watch("client");
  const folders = watch("folders");

  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  const handleUpload = async (selectedFiles, checked) => {
    if (!selectedClient) {
      toast.error("Selecione um cliente");
      return;
    }

    const updatedStatus = { ...uploadStatus };
    const updatedProgress = { ...uploadProgress };

    for (let i = 0; i < selectedFiles.length; i++) {
      const { id, file } = selectedFiles[i];
      if (!checked.includes(id) || uploadStatus[id] === "done") continue;

      updatedStatus[id] = "uploading";

      const formData = new FormData();
      formData.append("file", file);
      formData.append("client_id", selectedClient.id);
      formData.append("folder_id", folderId ? folderId : folders[0]);
      if (csvReference?.trim()) {
        formData.append("event_csv_reference", csvReference?.trim());
      }

      try {
        const response = await newApi.post(
          "/v1/event-texts/metadata/upload",
          formData,
          {
            onUploadProgress: ({ loaded, total }) => {
              updatedProgress[id] = Math.round((loaded * 100) / total);
              setUploadProgress({ ...updatedProgress });
            },
          },
        );

        if (response?.status !== 201) {
          toast.error(
            "Erro ao realizar o upload dos arquivos de metadados, tente novamente mais tarde.",
          );
          updatedStatus[id] = "error";
          continue;
        }

        setValue("metadata_csv_reference", response?.data?.csv_url);
        setValue("metadata_source_files", [
          ...new Set([...metadataSourceFiles, file.name]),
        ]);

        if (response?.data?.columns) {
          setValue("metadata_columns", response.data.columns);

          try {
            const folderToAnalyze = folderId ? folderId : folders[0];

            let attempt = 0;
            const maxAttempts = 3;
            let lastError = null;
            let summary = null;

            while (attempt < maxAttempts) {
              try {
                if (attempt > 0) {
                  const waitTime = 30000 * Math.pow(2, attempt - 1);
                  await new Promise((resolve) => setTimeout(resolve, waitTime));
                }

                const { data } = await newApi.get(
                  "v1/events/metadata/summary",
                  {
                    params: {
                      folderIds: [folderToAnalyze],
                      origin: "LOCAL",
                      clientId: selectedClient.id,
                    },
                    timeout: 180000,
                  },
                );
                summary = data;
                lastError = null;
                break;
              } catch (err) {
                lastError = err;
                const statusCode = err?.response?.status || err?.status;
                const isTimeout =
                  err?.code === "ECONNABORTED" ||
                  err?.code === "ERR_NETWORK" ||
                  statusCode === 504 ||
                  statusCode === 408 ||
                  (err?.message &&
                    (err.message.toLowerCase().includes("timeout") ||
                      err.message.toLowerCase().includes("network error")));
                attempt++;
                if (!isTimeout || attempt >= maxAttempts) break;
              }
            }

            if (!summary) throw lastError;

            const files = Array.isArray(summary?.files) ? summary.files : [];
            const foldersMap = new Map();
            const allColumns = new Set();

            files.forEach((file) => {
              const fId = file.folderId || folderToAnalyze;
              if (!foldersMap.has(fId)) {
                foldersMap.set(fId, {
                  folderId: fId,
                  folderName: fId,
                  metadataFiles: [],
                });
              }
              const folderObj = foldersMap.get(fId);
              folderObj.metadataFiles.push({
                fileName: file.fileName,
                totalRecords: file.totalRecords,
                columns: Array.isArray(file.columns) ? file.columns : [],
              });
              (file.columns || []).forEach((c) => allColumns.add(c));
            });

            const allFolders = Array.from(foldersMap.values());

            if (allFolders.length > 0) {
              setValue("metadata_source_data", {
                columns: Array.from(allColumns),
                folders: allFolders,
              });
            }
          } catch (analysisError) {
            console.error("Error fetching metadata analysis:", analysisError);
            setValue("metadata_source_data", {
              columns: response.data.columns,
              folders: [],
            });
          }
        }
        updatedStatus[id] = "done";
      } catch (err) {
        console.error(err);
        updatedStatus[id] = "error";
        if (
          err.response?.data?.message &&
          typeof err.response.data.message === "string" &&
          err.response.data.message.includes("Missing headers")
        ) {
          toast.error(
            "Campos obrigatórios não foram incluídos: " +
              err.response.data.message.replace("Missing headers: ", ""),
          );
          continue;
        }
        if (
          err.response?.data?.message &&
          typeof err.response.data.message === "string" &&
          err.response.data.message.includes(
            "CSV Should contain at least one or more metadata",
          )
        ) {
          toast.error("CSV Deve conter pelomenos 1 coluna além do EVENT_ID ");
          continue;
        }
        if (err?.response?.data?.code === 5003) {
          toast.error(
            "Erro ao realizar o upload dos arquivos de metadados: Arquivo inválido.",
          );
          continue;
        }
        if (err?.response?.data?.code === 5004) {
          toast.error(
            "Erro ao realizar o upload dos arquivos de metadados: Não foram encontrados eventos para um ou mais metadados.",
          );
          continue;
        }
        if (err?.response?.data?.code === 5005) {
          toast.error(
            "Erro ao realizar o upload dos arquivos de metadados: Não foram encontrados eventos.",
          );
          continue;
        }
        toast.error(
          "Erro ao realizar o upload dos arquivos de metadados: " +
            err.response?.data?.message,
        );
      }
    }

    setUploadStatus(updatedStatus);
  };

  const handleRemoveFiles = (
    updatedFiles,
    removedFileName,
    isUploadedFile = false,
  ) => {
    if (isUploadedFile) {
      const updatedUploadedFiles = Array.isArray(updatedFiles)
        ? updatedFiles
        : (metadataSourceFiles || []).filter(
            (fileName) => fileName !== removedFileName,
          );
      setValue("metadata_source_files", updatedUploadedFiles);

      if (updatedUploadedFiles.length === 0) {
        setValue("metadata_csv_reference", null);
        setValue("metadata_columns", []);
      }

      const fileId = removedFileName;
      const newUploadStatus = { ...uploadStatus };
      const newUploadProgress = { ...uploadProgress };
      delete newUploadStatus[fileId];
      delete newUploadProgress[fileId];
      setUploadStatus(newUploadStatus);
      setUploadProgress(newUploadProgress);
    }
  };

  return (
    <div className="d-flex flex-column">
      <label className="form-control-label mt-2">
        Exemplo formato do arquivo
      </label>
      <div className="pl-2 mt-2" style={{ fontSize: "0.8em" }}>
        <ul style={{ paddingLeft: "1rem", margin: 0 }}>
          <li>
            A coluna <b>EVENT_ID</b> é <b>obrigatória</b>, pois é utilizada para
            relacionar os metadados com os eventos.
          </li>
          <li>
            O arquivo deve conter <b>pelo menos uma coluna além de EVENT_ID</b>.
          </li>
          <li>Todas as outras colunas são opcionais.</li>
          <li>
            Para informar o <b>ID do consultor</b>, utilize a coluna{" "}
            <b>CONSULTANT_EXTERNAL_ID</b>.
          </li>
          <li>
            Para informar o <b>ID do supervisor</b>, utilize a coluna{" "}
            <b>SUPERVISOR_EXTERNAL_ID</b>.
          </li>
        </ul>
      </div>

      <div
        className="styled-scroll mt-2"
        style={{
          width: "100%",
          height: "250px",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        <Table
          style={{
            tableLayout: "fixed",
            width: "100%",
          }}
        >
          <thead className="thead-ligth">
            <tr>
              <th scope="col" style={{ fontSize: "0.6em" }}>
                EVENT_ID
              </th>
              <th scope="col" style={{ fontSize: "0.6em" }}>
                CONSULTANT_EXTERNAL_ID
              </th>
              <th scope="col" style={{ fontSize: "0.6em" }}>
                SUPERVISOR_EXTERNAL_ID
              </th>
              <th scope="col" style={{ fontSize: "0.6em" }}>
                OCORRENCIA
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Atendimento-3054
              </td>
              <td>silva.fulano</td>
              <td>santos.ciclano</td>
              <td>Sem retorno</td>
            </tr>
            <tr>
              <td
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Fila-114528
              </td>
              <td>silva.fulano</td>
              <td>santos.ciclano</td>
              <td>Cobrança</td>
            </tr>
            <tr>
              <td
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Fila-0985
              </td>
              <td>silva.fulano</td>
              <td>santos.ciclano</td>
              <td>Reclamação</td>
            </tr>
          </tbody>
        </Table>
      </div>
      <div className="mb-3">
        <label className="form-control-label">
          Arquivos de texto contendo os metadados
        </label>
        <FilePicker
          multiple
          accept="text/csv"
          uploadedFiles={metadataSourceFiles}
          onUpload={handleUpload}
          onFilesRemoved={handleRemoveFiles}
          uploadProgress={uploadProgress}
          uploadStatus={uploadStatus}
          maxFiles={MAX_CSV_FILES_LIMIT}
          maxFileSizeInMB={MAX_FILE_SIZE_MB}
          folderId={folderId ? folderId : folders[0]}
          clientId={selectedClient.id}
        />
      </div>

      {/* Display metadata columns if available */}
      {metadataColumns.length > 0 && (
        <div className="card mt-3 mb-3">
          <div className="card-header bg-light">
            <h5 className="mb-0" style={{ fontSize: "0.9rem" }}>
              <i className="fas fa-columns me-2"></i> Colunas do arquivo de
              metadados
            </h5>
          </div>
          <div className="card-body p-3">
            <div className="d-flex flex-wrap gap-2">
              {metadataColumns.map((column, index) => (
                <span
                  key={index}
                  className="badge badge-pill"
                  style={{
                    backgroundColor:
                      column === "EVENT_ID" ? "#2dce89" : "#5e72e4",
                    color: "white",
                    fontSize: "0.75rem",
                    padding: "0.5rem 0.75rem",
                    fontWeight: "500",
                    marginRight: "0.1rem",
                    marginTop: "0.1rem",
                  }}
                >
                  {column}
                  {column === "EVENT_ID" && (
                    <i className="fas fa-check-circle ms-1"></i>
                  )}
                </span>
              ))}
            </div>
            <div className="mt-2">
              <small className="text-muted">
                <i className="fas fa-info-circle me-1"></i> Total de{" "}
                {metadataColumns.length} coluna
                {metadataColumns.length !== 1 ? "s" : ""} encontrada
                {metadataColumns.length !== 1 ? "s" : ""}
              </small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MetadataIsFilterComponent() {
  const { control } = useFormContext();

  return (
    <Controller
      name="metadataIsFilter"
      control={control}
      defaultValue={false}
      render={({ field }) => (
        <div title="Quando ativado, somente eventos com metadados entram no processamento.">
          <FormControlLabel
            control={
              <Switch
                {...field}
                checked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                color="primary"
              />
            }
            label="Utilizar metadados como filtro?"
            sx={{ ".MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
          />
        </div>
      )}
    />
  );
}

function StepEnrichment({ next, back }) {
  const { watch, control, setValue } = useFormContext();
  const dataSource = watch("data_source");
  const dataSourceType = watch("metadata_source_type");
  const metadataSourceFiles = watch("metadata_source_files") || [];
  const metadataSourceQuery = watch("metadataSourceQuery") || "";
  const selectedClient = watch("client");
  const folders = watch("folders");
  const isCopy = watch("IsCopy");

  const [metadataPreview, setMetadataPreview] = useState(null);
  const [openFolders, setOpenFolders] = useState({});
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const abortControllerRef = React.useRef(null);
  const cachedMetadataRef = React.useRef(null);

  const [copyWarningTooltipOpen, setCopyWarningTooltipOpen] = useState(false);

  const toggleCopyWarningTooltip = () =>
    setCopyWarningTooltipOpen((prev) => !prev);

  const getCacheKey = (folderIds) => {
    const sortedIds = [...folderIds].sort().join(",");
    return `metadata_preview_${sortedIds}`;
  };

  const saveToCache = (folderIds, data) => {
    try {
      const cacheKey = getCacheKey(folderIds);
      const cacheData = {
        timestamp: Date.now(),
        data: data,
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.warn("Failed to save metadata to localStorage:", error);
    }
  };

  const loadFromCache = (folderIds) => {
    try {
      const cacheKey = getCacheKey(folderIds);
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const { timestamp, data } = JSON.parse(cached);
      const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

      if (Date.now() - timestamp > CACHE_DURATION) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return data;
    } catch (error) {
      console.warn("Failed to load metadata from localStorage:", error);
      return null;
    }
  };

  const isQueryValid = (q) => {
    const sql = q.trim().toLowerCase();
    return sql.startsWith("select") && sql.includes("from");
  };

  const cancelMetadataRequest = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoadingMetadata(false);
      toast.info("Busca de metadados cancelada.");
    }
  };

  const isDone = useMemo(() => {
    // Se não há tipo de fonte de metadados selecionado, permite pular
    if (!dataSourceType) {
      return false;
    }

    if (dataSource === "LOCAL") {
      return metadataSourceFiles.length > 0;
    }

    if (dataSource === "CLOUD") {
      if (dataSourceType === "csv") {
        return true;
      }
      if (dataSourceType === "query") {
        return isQueryValid(metadataSourceQuery);
      }

      return metadataSourceFiles.length > 0;
    }

    return false;
  }, [
    dataSource,
    dataSourceType,
    metadataSourceFiles.length,
    metadataSourceQuery,
  ]);

  const getMetadataDetailsPreview = async () => {
    const cachedData = loadFromCache(folders);
    if (cachedData) {
      setMetadataPreview(cachedData.preview);
      setValue("metadata_source_data", cachedData.sourceData);
      setValue("metadata_source_type", "csv");
      if (cachedData.files?.length) {
        setValue("metadata_source_files", cachedData.files);
      }
      if (cachedData.columns?.length) {
        setValue("metadata_columns", cachedData.columns);
      }
      if (cachedData.openFolders) {
        setOpenFolders(cachedData.openFolders);
      }
      toast.success(
        `Metadados carregados do cache (${cachedData.files?.length || 0} arquivo(s))`,
        { autoClose: 2000 },
      );
      return;
    }

    setIsLoadingMetadata(true);
    abortControllerRef.current = new AbortController();

    toast.info(
      "Buscando metadados disponíveis, aguarde um momento o processo poderá levar alguns segundos...",
      { autoClose: 12000 },
    );
    if (dataSource === "CLOUD") {
      try {
        // Single request with new response format: { files: MetadataFile[] }
        let attempt = 0;
        const maxAttempts = 3;
        let lastError = null;
        let data = null;

        while (attempt < maxAttempts) {
          try {
            if (attempt > 0) {
              const waitTime = 30000 * Math.pow(2, attempt - 1); // 30s, 60s
              await new Promise((resolve) => setTimeout(resolve, waitTime));
            }

            const response = await newApi.get("v1/events/metadata/summary", {
              params: {
                folderIds: folders,
                origin: dataSource,
                clientId: selectedClient.id,
              },
              timeout: 180000,
              signal: abortControllerRef.current?.signal,
            });
            data = response.data;
            lastError = null;
            break;
          } catch (err) {
            lastError = err;
            const statusCode = err?.response?.status || err?.status;
            const isTimeout =
              err?.code === "ECONNABORTED" ||
              err?.code === "ERR_NETWORK" ||
              statusCode === 504 ||
              statusCode === 408 ||
              (err?.message &&
                (err.message.toLowerCase().includes("timeout") ||
                  err.message.toLowerCase().includes("network error")));

            attempt++;
            if (!isTimeout || attempt >= maxAttempts) break;
          }
        }
        if (!data) {
          throw lastError;
        }

        const files = Array.isArray(data?.files) ? data.files : [];
        const foldersMap = new Map();
        const allColumns = new Set();

        files.forEach((file) => {
          const fId = file.folderId;
          if (!foldersMap.has(fId)) {
            foldersMap.set(fId, {
              folderId: fId,
              folderName: fId,
              metadataFiles: [],
            });
          }
          const folderObj = foldersMap.get(fId);
          folderObj.metadataFiles.push({
            fileName: file.fileName,
            totalRecords: file.totalRecords,
            columns: Array.isArray(file.columns) ? file.columns : [],
          });
          (file.columns || []).forEach((c) => allColumns.add(c));
        });

        const allFolders = Array.from(foldersMap.values());

        const hasMetadataFiles = allFolders.some(
          (folder) => folder?.metadataFiles?.length,
        );

        if (!hasMetadataFiles) {
          setMetadataPreview(null);
          cachedMetadataRef.current = null;
          toast.info("Nenhum arquivo de metadados encontrado nas pastas.");
          return;
        }

        const finalData = {
          folders: allFolders,
          columns: Array.from(allColumns),
          pagination: {
            totalRows: allFolders.reduce(
              (sum, folder) =>
                sum +
                (folder.metadataFiles || []).reduce(
                  (s, f) => s + (f.totalRecords || 0),
                  0,
                ),
              0,
            ),
          },
        };

        setValue("metadata_source_data", finalData);
        setValue("metadata_source_type", "csv");
        setMetadataPreview(finalData);

        const collectedFiles = allFolders.flatMap((folder) =>
          (folder?.metadataFiles || []).map((file) => file.fileName),
        );

        const uniqueFiles = [
          ...new Set([...(metadataSourceFiles || []), ...collectedFiles]),
        ];

        if (collectedFiles.length) {
          setValue("metadata_source_files", uniqueFiles);
        }

        if (finalData.columns.length) {
          setValue("metadata_columns", finalData.columns);
        }

        const foldersState = {};
        allFolders.forEach((folder, index) => {
          if (folder?.metadataFiles?.length) {
            foldersState[index] = index === 0;
          }
        });

        setOpenFolders(foldersState);

        // Save to localStorage cache
        saveToCache(folders, {
          preview: finalData,
          sourceData: finalData,
          files: uniqueFiles,
          columns: finalData.columns,
          openFolders: foldersState,
        });

        toast.success(
          `${collectedFiles.length} arquivo(s) de metadados encontrado(s)`,
          { autoClose: 3000 },
        );
      } catch (error) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
          console.log("Requisição de metadados cancelada pelo usuário");
          return;
        }
        console.error("Erro ao buscar metadados:", error);
        toast.error(
          "Erro ao buscar metadados disponíveis. Tente novamente mais tarde.",
        );
      } finally {
        setIsLoadingMetadata(false);
        abortControllerRef.current = null;
      }
    } else {
      setIsLoadingMetadata(false);
    }
  };

  useEffect(() => {
    if (dataSource === "CLOUD" && folders?.length > 0) {
      getMetadataDetailsPreview();
    }
  }, [dataSource, JSON.stringify((folders || []).sort())]);

  const toggleFolderSection = (index) => {
    setOpenFolders((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const hasMetadataPreview = useMemo(
    () =>
      !!metadataPreview?.folders?.some(
        (folder) => folder?.metadataFiles && folder.metadataFiles.length > 0,
      ),
    [metadataPreview],
  );

  const shouldShowMetadataPreview =
    dataSource === "CLOUD" && hasMetadataPreview;

  return (
    <div className="card bg-secondary shadow">
      <div className="card-header bg-white border-0">
        <h3 className="mb-0">Metadados</h3>
      </div>

      <div className="card-body">
        {isLoadingMetadata && (
          <div
            className="d-flex justify-content-center align-items-center mb-4 p-4"
            style={{
              backgroundColor: "#f8f9fa",
              borderRadius: "8px",
              border: "1px solid #dee2e6",
            }}
          >
            <div className="spinner-border text-primary mr-3" role="status">
              <span className="sr-only">Carregando...</span>
            </div>
            <div className="flex-grow-1">
              <h5 className="mb-1">Buscando metadados disponíveis...</h5>
              <p className="mb-0 text-muted" style={{ fontSize: "0.85rem" }}>
                Este processo pode levar alguns segundos. Por favor aguarde.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger ml-3"
              onClick={cancelMetadataRequest}
              title="Cancelar busca de metadados"
            >
              <i className="fas fa-times mr-1"></i>
              Cancelar
            </button>
          </div>
        )}
        <form>
          <div className="mb-4">
            <label className="form-control-label">
              Fonte
              {isCopy && (
                <i
                  className="fas fa-exclamation-triangle text-warning ml-2"
                  id="copyWarningEnrichment"
                  style={{ cursor: "pointer" }}
                />
              )}
              {isCopy && (
                <Tooltip
                  placement="right"
                  isOpen={copyWarningTooltipOpen}
                  target="copyWarningEnrichment"
                  toggle={toggleCopyWarningTooltip}
                >
                  <div>
                    <strong>⚠️ Cópia de Pipeline</strong>
                    <br />
                    {dataSourceType === "query" ? (
                      <>
                        Esta é uma cópia - revise a query e ajuste as datas se
                        necessário.
                        <br />
                        Confirme se as informações estão corretas.
                      </>
                    ) : (
                      <>
                        Esta é uma cópia - você pode adicionar novos metadados
                        <br />
                        ou pular esta etapa se não precisar deles.
                      </>
                    )}
                  </div>
                </Tooltip>
              )}
            </label>
            <Controller
              name="metadata_source_type"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  options={metadataSourceOptions}
                  placeholder="Selecione a fonte dos metadados"
                  onChange={(opt) => field.onChange(opt ? opt.value : "")}
                  value={
                    metadataSourceOptions.find(
                      (opt) => opt.value === field.value,
                    ) || null
                  }
                  isDisabled={metadataSourceFiles.length > 0}
                  styles={selectStyles}
                  isClearable
                />
              )}
            />
          </div>

          {/* LOCAL + CSV upload ------------------------------------------- */}
          {(dataSourceType === "csv" ||
            dataSourceType === "csv" ||
            dataSourceType === "local") &&
            dataSource === "LOCAL" && <CSVComponent />}

          {/* LOCAL + FILE upload ------------------------------------------ */}
          {dataSourceType === "local" && dataSource === "CLOUD" && (
            <CSVComponent />
          )}

          {/* CLOUD + QUERY textarea --------------------------------------- */}
          {dataSource === "CLOUD" && dataSourceType === "query" && (
            <div className="mb-3">
              <label
                className="form-control-label"
                htmlFor="metadata_source_query"
              >
                Digite a query*
              </label>
              <label style={{ fontSize: "0.8em", margin: 0 }}>
                {" - "}A query deve incluir o campo <strong>event_id</strong>.
              </label>
              <hr className="my-2" />
              <div>
                <small className="d-block mb-1">
                  Para enriquecer o evento com informações adicionais, você pode
                  utilizar os seguintes aliases opcionais:
                </small>
                <ul
                  className="ps-3 mb-2"
                  style={{ listStyleType: "disc", fontSize: "0.85em" }}
                >
                  <li className="mb-1">
                    <strong>supervisor_external_id</strong>: Identificador do
                    supervisor vinculado ao evento.
                  </li>
                  <li className="mb-1">
                    <strong>consultant_external_id</strong>: Identificador do
                    consultor vinculado ao evento.
                  </li>
                  <li>
                    <strong>event_date</strong>: Identificador da data
                    relacionada ao evento.
                  </li>
                </ul>
              </div>

              <Controller
                name="metadataSourceQuery"
                control={control}
                rules={{
                  required: "Por favor forneça a query.",
                }}
                render={({ field, fieldState }) => (
                  <>
                    <textarea
                      {...field}
                      id="metadata_source_query"
                      className="form-control"
                      style={{
                        width: "100%",
                        height: "250px",
                        resize: "none",
                        backgroundColor: "#fff",
                        padding: "10px",
                      }}
                      placeholder="SELECT … FROM … WHERE …"
                    />
                    {fieldState.error && (
                      <span
                        className="text-danger"
                        style={{ fontSize: "0.8em" }}
                      >
                        {fieldState.error.message}
                      </span>
                    )}
                    {!fieldState.error &&
                      metadataSourceQuery &&
                      !isQueryValid(metadataSourceQuery) && (
                        <span
                          className="text-warning"
                          style={{ fontSize: "0.8em" }}
                        >
                          A query deve começar com <code>SELECT</code>,
                          mencionar uma tabela fonte com <code>FROM</code> e
                          conter <code>event_id</code>.
                        </span>
                      )}
                  </>
                )}
              />
            </div>
          )}

          {/* CLOUD + CSV reminder ---------------------------------------- */}
          {dataSource === "CLOUD" && dataSourceType === "csv" && (
            <p style={{ fontSize: "0.8em", margin: 0 }}>
              Com <strong>Fonte: CSV</strong> e{" "}
              <strong>Dados: Nuvem Concentrix</strong>, o arquivo deve estar
              presente nas pastas selecionadas no passo <em>Fonte de Dados</em>.
            </p>
          )}

          <MetadataIsFilterComponent />

          {shouldShowMetadataPreview && (
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ fontSize: "1rem" }}>
                  Pré-visualização dos metadados encontrados
                </h5>
                {metadataPreview?.pagination?.totalRows ? (
                  <small className="text-muted">
                    {metadataPreview.pagination.totalRows} registro
                    {metadataPreview.pagination.totalRows !== 1 ? "s" : ""}{" "}
                    encontrados
                  </small>
                ) : null}
              </div>
              <p className="text-muted mb-3" style={{ fontSize: "0.8em" }}>
                Clique no nome da pasta para expandir os arquivos analisados.
              </p>

              <div id="metadataPreviewAccordion">
                {(metadataPreview?.folders || [])
                  .filter((folder) => folder?.metadataFiles?.length)
                  .map((folder, folderIndex) => {
                    const folderFiles = folder.metadataFiles || [];
                    const columnsToShow =
                      (folder?.columns && folder.columns.length
                        ? folder.columns
                        : metadataPreview?.columns) || [];
                    const isOpen = !!openFolders[folderIndex];

                    return (
                      <div
                        className="card mb-2"
                        key={
                          folder.folderId ||
                          `${folder.folderName || "folder"}-${folderIndex}`
                        }
                      >
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <button
                            type="button"
                            className="btn btn-link text-left p-0 text-decoration-none"
                            onClick={() => toggleFolderSection(folderIndex)}
                            style={{
                              fontSize: "0.92rem",
                            }}
                          >
                            <i
                              className={`fas ${
                                isOpen ? "fa-chevron-up" : "fa-chevron-down"
                              } mr-2`}
                            />
                            {folder.folderName || "Pasta sem nome"}
                          </button>
                          <span className="badge badge-info">
                            {folderFiles.length} arquivo
                            {folderFiles.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <Collapse isOpen={isOpen}>
                          <div className="card-body">
                            {columnsToShow.length > 0 && (
                              <div className="mb-3">
                                <strong style={{ fontSize: "0.85rem" }}>
                                  Colunas encontradas
                                </strong>
                                <div
                                  className="d-flex flex-wrap mt-2"
                                  style={{ gap: "0.35rem" }}
                                >
                                  {columnsToShow.map((column) => (
                                    <span
                                      key={column}
                                      className="badge badge-pill"
                                      style={{
                                        backgroundColor:
                                          column === "EVENT_ID"
                                            ? "#2dce89"
                                            : "#5e72e4",
                                        color: "#fff",
                                        fontSize: "0.7rem",
                                        padding: "0.4rem 0.65rem",
                                      }}
                                    >
                                      {column}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {folderFiles.map((file, fileIndex) => {
                              const rows = Array.isArray(file?.data)
                                ? file.data
                                : [];

                              return (
                                <div
                                  key={`${file.fileName || "metadata"}-${folderIndex}-${fileIndex}`}
                                  className={fileIndex === 0 ? "" : "mt-4"}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <h6
                                        className="mb-1"
                                        style={{ fontSize: "0.85rem" }}
                                      >
                                        {file.fileName}
                                      </h6>
                                      {file.filePath && (
                                        <small className="text-muted d-block">
                                          {file.filePath}
                                        </small>
                                      )}
                                    </div>
                                    <span className="badge badge-secondary">
                                      {file.totalRecords ?? rows.length}{" "}
                                      registro
                                      {(file.totalRecords ?? rows.length) !== 1
                                        ? "s"
                                        : ""}
                                    </span>
                                  </div>
                                  {/* <div
                                    className="styled-scroll mt-2"
                                    style={{
                                      maxHeight: "250px",
                                      overflowY: "auto",
                                    }}
                                  >
                                    <Table
                                      responsive
                                      size="sm"
                                      className="mb-0"
                                    >
                                      <thead>
                                        <tr>
                                          {tableColumns.map((column) => (
                                            <th
                                              key={column}
                                              style={{ fontSize: "0.7rem" }}
                                            >
                                              {column}
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {rows.length > 0 ? (
                                          rows.map((row, rowIndex) => (
                                            <tr
                                              key={`${file.fileName || "row"}-${rowIndex}`}
                                              style={{ whiteSpace: "nowrap" }}
                                            >
                                              {tableColumns.map((column) => {
                                                const cellValue = row?.[column];
                                                const normalizedValue =
                                                  cellValue === null ||
                                                  cellValue === undefined ||
                                                  cellValue === ""
                                                    ? "-"
                                                    : typeof cellValue ===
                                                        "object"
                                                      ? JSON.stringify(
                                                          cellValue,
                                                        )
                                                      : String(cellValue);

                                                return (
                                                  <td
                                                    key={`${column}-${rowIndex}`}
                                                    style={{
                                                      fontSize: "0.75rem",
                                                    }}
                                                  >
                                                    {normalizedValue}
                                                  </td>
                                                );
                                              })}
                                            </tr>
                                          ))
                                        ) : (
                                          <tr>
                                            <td
                                              colSpan={Math.max(
                                                tableColumns.length,
                                                1,
                                              )}
                                              className="text-center text-muted"
                                              style={{ fontSize: "0.75rem" }}
                                            >
                                              Nenhum dado encontrado.
                                            </td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </Table>
                                  </div> */}
                                </div>
                              );
                            })}
                          </div>
                        </Collapse>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Navigation buttons ------------------------------------------ */}
          <div className="d-flex justify-content-between gap-1 mt-4">
            <button type="button" className="btn btn-secondary" onClick={back}>
              Voltar
            </button>

            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={next}
            >
              Pular
            </button>

            <button
              type="button"
              className="btn btn-success"
              onClick={next}
              disabled={!isDone}
              title={
                isDone
                  ? "Seguir para o próximo passo"
                  : "Complete este passo corretamente"
              }
            >
              Próximo
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default StepEnrichment;
